import { memo, useCallback, useMemo } from 'react';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  mediaMin,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import {
  black,
  blue01,
  blue02,
  gray02,
  gray04,
  gray06,
  primary,
  white,
} from 'presentations/views/styles/color';
import { DateTime, formatToTimeShort } from 'infras/datetime';
import styled from '@emotion/styled';
import EventCell from '../EventCell';
import Icon from 'presentations/views/components/atoms/Icon';
import { useDayTimeGrid } from './hooks';
import { imagePath } from 'presentations/assets';
import { css } from '@emotion/react';
import { isMobileApp } from 'presentations/views/helpers/electron';
import { Behavior } from '../reducer';

interface Props {
  className?: string;
  startedAt: DateTime;
}

const Component = memo((props: Props) => {
  const { state, actions } = useDayTimeGrid({
    startedAt: props.startedAt,
  });
  const addIconUrl = useMemo(() => {
    return isMobileApp()
      ? undefined
      : imagePath('pages/dashboard/calendars/add_slot.svg');
  }, []);
  const removeIconUrl = useMemo(() => {
    return isMobileApp()
      ? undefined
      : imagePath('pages/dashboard/calendars/remove_slot.svg');
  }, []);
  const unixtime = useMemo(() => state.now.toSeconds(), [state.now]);

  const renderNowBorder = useCallback(
    (date: DateTime) => {
      const diff = unixtime - date.toSeconds();
      const diffRatio = diff / 60 / state.rulerStep;
      if (0 <= diffRatio && diffRatio < 1) {
        return (
          <NowBorder ratio={diffRatio}>
            <NowBorderCircle />
          </NowBorder>
        );
      }
      return null;
    },
    [state.rulerStep, unixtime],
  );

  return (
    <Calendar
      className={props.className}
      maxRow={state.maxRow}
      maxCol={state.dates.length}
    >
      {state.frames.map((frame: { date: DateTime }, i: number) => {
        const diff = unixtime - frame.date.toSeconds();
        return (
          <SlotTimeCell
            key={i}
            isDivider={frame.date.minute === 0}
            isTop={frame.date.hour === 0}
            iconUrl={addIconUrl}
            isDisabled={diff > 0}
            isShowBorder={frame.date.minute % state.rulerBorderStep === 0}
          >
            <DraggableCell
              draggable
              data-frame-id={i}
              data-slot-id={i}
              data-all-day={0}
              onDragStart={actions.onDayDragStart}
              onDragEnter={actions.onDayDragEnter}
              onDragEnd={actions.onDayDragEnd}
              onClick={actions.onClickAddSlot}
            />
            {renderNowBorder(frame.date)}
          </SlotTimeCell>
        );
      })}
      {!state.isLoading &&
        state.events.map((event) => {
          const timeText = formatToTimeShort(event.startedAt);
          return (
            <EventCell
              key={event.layoutId}
              data-event-id={event.eventId}
              x={event.x}
              y={event.y}
              ySpan={event.ySpan}
              xRatio={event.xRatio}
              xSpanRatio={event.xSpanRatio}
              xMargin={event.xMargin}
              color={event.color}
              colors={(event.mergedEvents || []).map((event) => event.color)}
              name={event.name}
              timeText={timeText}
              icon={
                event.behavior === Behavior.Work && (
                  <ServiceIcon name="check-circle" />
                )
              }
            />
          );
        })}
      {state.currentDragGroup && (
        <DraggingGroupCell
          draggable
          x={state.currentDragGroup.x}
          xSpan={state.currentDragGroup.xSpan}
          y={state.currentDragGroup.y}
          ySpan={state.currentDragGroup.ySpan}
          isShowSlot
        >
          <GroupTime>
            <GroupTimeText>
              {formatToTimeShort(state.currentDragGroup.startedAt)}
              <Haifun>-</Haifun>
              {formatToTimeShort(state.currentDragGroup.endedAt)}
            </GroupTimeText>
          </GroupTime>
        </DraggingGroupCell>
      )}
      {state.isShowGroup &&
        state.selectedGroups.map((selectedGroup, i) => {
          return (
            <SelectedGroupCell
              key={i}
              draggable
              x={selectedGroup.x}
              xSpan={selectedGroup.xSpan}
              y={selectedGroup.y}
              ySpan={selectedGroup.ySpan}
              isShowSlot={state.isShowSlot}
              iconUrl={state.isShowSlot ? undefined : removeIconUrl}
            >
              <GroupTime>
                <GroupTimeText>
                  {formatToTimeShort(selectedGroup.startedAt)}
                  <Haifun>-</Haifun>
                  {formatToTimeShort(selectedGroup.endedAt)}
                </GroupTimeText>
                {selectedGroup.ySpan > state.yStep && (
                  <GroupDeleteButton
                    data-group-id={i}
                    data-date-id={selectedGroup.startedAt.toFormat('yyyyMMdd')}
                    onClick={actions.onClickDeleteSlotGroup}
                  >
                    <GroupDeleteIcon name="close-circle" />
                  </GroupDeleteButton>
                )}
              </GroupTime>
            </SelectedGroupCell>
          );
        })}
      {state.selectedSlots.map((slot) => {
        return (
          <SlotCell
            key={slot.id}
            data-slot-id={slot.id}
            x={slot.x}
            xSpan={1}
            y={slot.y}
            ySpan={slot.ySpan}
            onClick={actions.onClickSlot}
            iconUrl={removeIconUrl}
            isShowSlot={state.isShowSlot}
          />
        );
      })}
    </Calendar>
  );
});

const Calendar = styled.div<{ maxRow: number; maxCol: number }>`
  width: inherit;
  flex: 1 1 0%;
  display: grid;
  grid-template-rows: ${(props) => `repeat(${props.maxRow}, minmax(8px, 1fr))`};
  grid-template-columns: ${(props) => `repeat(${props.maxCol}, 1fr)`};
  grid-auto-flow: column;
  position: relative;
  border-bottom: 1px solid ${gray04};
`;

export const TimeCell = styled.div<{
  isDivider: boolean;
  isShowBorder: boolean;
  isTop: boolean;
  iconUrl?: string | null;
  isDisabled?: boolean;
  isHoliday?: boolean;
}>`
  position: relative;
  border-color: ${gray04};
  border-left-width: 1px;
  border-left-style: solid;
  border-top-width: ${(props) =>
    !props.isShowBorder || (props.isTop && props.isDivider) ? 0 : '1px'};
  border-top-style: ${(props) => (props.isDivider ? 'solid' : 'dotted')};
  cursor: ${(props) =>
    props.isDisabled ? 'not-allowed' : `url(${props.iconUrl}) 10 10, pointer`};
  background-color: ${(props) => props.isHoliday && gray06};
  color: ${(props) => props.isHoliday && gray02};
`;
export const SlotTimeCell = styled(TimeCell)`
  z-index: 1;
  ${mediaMin.smartphone} {
    ${(props) =>
      props.isDisabled
        ? css`
            user-select: none;
            :hover {
              background-color: transparent;
            }
          `
        : css`
            :hover {
              border-radius: ${borderRadius(6)};
              background-color: ${blue02(props)}bb;
              border: 1px dotted ${primary(props)};
            }
          `}
  }
`;

export const SelectedGroupCell = styled.div<{
  x: number;
  xSpan: number;
  y: number;
  ySpan: number;
  isShowSlot: boolean;
  iconUrl?: string;
}>`
  grid-area: ${(props) =>
    `${props.y + 1} / ${props.x + 1} / span ${props.ySpan} / span ${
      props.xSpan
    }`}; // 縦位置 / 横位置 / 縦枠幅 / 横枠幅
  width: calc(100% - 1px);
  height: calc(100% - 0px);
  position: absolute;
  z-index: 2;
  top: 0px;
  left: 0px;
  border: 1px solid ${primary};
  color: ${primary};
  padding: ${spacing(2)} ${spacing(4)};
  border-radius: ${borderRadius(6)};
  background-color: ${(props) =>
    props.isShowSlot ? `${white(props)}99` : `${blue02(props)}99`};
  display: flex;
  cursor: ${(props) =>
    props.iconUrl ? `url(${props.iconUrl}) 10 10, pointer` : 'pointer'};
`;
export const DraggingGroupCell = styled(SelectedGroupCell)`
  background-color: rgba(246, 250, 255, 0.6); // blue02
  user-select: none;
  pointer-events: none;
`;

export const SlotCell = styled.div<{
  x: number;
  xSpan: number;
  y: number;
  ySpan: number;
  iconUrl?: string;
  isShowSlot: boolean;
}>`
  grid-area: ${(props) =>
    `${props.y + 1} / ${props.x + 1} / span ${props.ySpan} / span ${
      props.xSpan
    }`}; // 縦位置 / 横位置 / 縦枠幅 / 横枠幅
  width: calc(100% - 8px);
  height: calc(100% - 8px);
  position: absolute;
  border-radius: ${borderRadius(6)};
  top: 4px;
  left: 4px;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${borderRadius(3)};
  cursor: ${(props) =>
    props.iconUrl ? `url(${props.iconUrl}) 10 10, pointer` : 'pointer'};
  border: 1px dotted ${primary};
  background-color: ${blue02}bb;
  ${mediaMin.tablet} {
    :hover {
      background-color: ${blue01};
    }
  }
  ${(props) =>
    !props.isShowSlot &&
    css`
      opacity: 0;
      ${mediaMin.tablet} {
        :hover {
          opacity: 1;
        }
      }
    `}
`;

export const GroupTime = styled.div`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  position: absolute;
  top: -12px;
  left: 0;
  z-index: ${zIndex(100)};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${mediaMax.desktop} {
    font-size: ${fontSize(12)};
  }
`;
export const GroupTimeText = styled.div`
  background-color: ${white};
  padding: 0 ${spacing(2)};
  margin-left: ${spacing(4)};
  line-height: 1;
`;
export const DraggableCell = styled.div`
  width: 100%;
  height: 100%;
  opacity: 0;
`;
export const Haifun = styled.span`
  margin: 0 ${spacing(2)};
  ${mediaMax.desktop} {
    margin: 0 1px;
  }
`;

export const NowBorderCircle = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${primary};
  position: absolute;
  top: -4px;
  left: -5px;
  z-index: ${zIndex(100)};
`;
const GroupDeleteButton = styled.div`
  position: absolute;
  top: -4px;
  right: 0px;
  display: flex;
  padding: 0;
  margin-right: ${spacing(4)};
  cursor: pointer;
`;
const GroupDeleteIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
  color: ${primary};
  background-color: ${white};
  border-radius: 50%;
`;
export const NowBorder = styled.div<{ ratio: number }>`
  width: 100%;
  height: 2px;
  background-color: ${primary};
  position: absolute;
  top: ${(props) => `calc(100% * ${props.ratio})`};
  left: 0;
  z-index: ${zIndex(100)};
`;
const ServiceIcon = styled(Icon)`
  width: 12px !important;
  height: 12px !important;
  color: inherit;
  margin-right: ${spacing(2)};
  :last-of-type {
    margin-right: 0;
  }
`;

export default Component;
